import React from 'react'
import { useNavigate } from 'react-router-dom'

import { SubMenuNavigation } from '@reapit-sales/navigation'
import { Badge, useSnack } from '@reapit/elements'
import { Privileges, Settings, SmsCreditsMenu, StaffMember } from '@reapit-sales/api-v2'
import { LegacyCRMIframeOpenPanel, PanelLegacyLink, ReactToLegacyCrmPostMessageAction } from '@reapit-sales/legacy-crm'
import { trackMixpanelAction, NAVIGATION, openNewPage } from '@reapit-sales/utilities'
import { buildCMSPageURL } from '@crm/components/cms/buildCMSPageURL'
import { ExportIcon } from '@reapit/elements/icons/export'

/**
 * `getUserMenu` generates a list of user menu items with corresponding actions.
 *
 * Props:
 * @param isLegacyCRMIframeLoaded (boolean): Indicates whether the legacy CRM iframe is loaded.
 * @param legacyCRMIframePageName (string): The page name for the legacy CRM iframe.
 * @param privileges (array): Privileges from APIv2 GET/configurations
 * @param setttings (array): Settings from APIv2 GET/configurations
 * @param staffMember (array): StaffMember from APIv2 GET/configurations
 * @param legacyCRMUrl - The URL of the legacy CRM iframe, used to send the postMessage.
 * @param connectLogoutRedirect (function): A function to handle the logout redirection, optionally accepting
 * a redirect URI.
 * @param navigate (function): A function to programmatically navigate to a route.
 *
 * Returns:
 * (SecondaryNavigationMenu[]): An array of menu items with labels and onClick functions, each opening a
 * specific panel or performing an action.
 *
 * This function:
 * 1. Constructs menu items with predefined labels such as "My Profile", "Manage SMS Credits", etc.
 * 2. Uses `LegacyCRMIframeOpenPanel` to open the corresponding panel when a menu item is clicked.
 * 3. Handles specific actions like opening a modal or redirecting the user for "Purchase SMS Credits" and
 * "Manage MFA".
 * 4. `hasAccess` is optional and only provide it if menu option require to be behind permission check
 */
export const getUserMenu = (
  isLegacyCRMIframeLoaded: boolean,
  legacyCRMIframePageName: string,
  privileges: Privileges,
  setttings: Settings,
  staffMember: StaffMember,
  smsCreditsMenu: SmsCreditsMenu,
  legacyCRMUrl: string,
  navigate: ReturnType<typeof useNavigate>,
  handleLogoutClick: () => void,
): SubMenuNavigation[] => {
  const { error } = useSnack()

  return [
    {
      label: 'group-profile',
      group: [
        {
          label: 'My Profile',
          onClick: () => {
            // Track the Mixpanel event for navigating to My Profile
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_MY_PROFILE, {
              Panel: NAVIGATION.PANEL_NAME.MY_PROFILE,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.MyProfile(staffMember.memId),
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Manage Subscription',
          hasAccess: privileges.manageSubscription?.canAccess,
          onClick: () => {
            // Track the Mixpanel event for navigating to Manage Subscription
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_MANAGE_SUBSCRIPTION, {
              Panel: NAVIGATION.PANEL_NAME.MANAGE_SUBSCRIPTION,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.ManageSubscription,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Manage SMS Credits',
          hasAccess: smsCreditsMenu?.show && smsCreditsMenu?.mode === 'manage',
          onClick: () => {
            // Track the Mixpanel event for navigating to Manage SMS Credits
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_MANAGE_SMS_CREDITS, {
              Panel: NAVIGATION.PANEL_NAME.MANAGE_SMS_CREDITS,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.ManageSMSCredits,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Purchase SMS Credits',
          hasAccess: smsCreditsMenu?.show && smsCreditsMenu?.mode === 'purchase',
          onClick: () => {
            // Track the Mixpanel event for navigating to Purchase SMS Credits
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_PURCHASE_SMS_CREDITS, {
              Panel: NAVIGATION.PANEL_NAME.PURCHASE_SMS_CREDITS,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              action: ReactToLegacyCrmPostMessageAction.ReactToIframeMessageOpenDeleteRecModal,
              deleteRecLink: PanelLegacyLink.PurchaseSMSCredits,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Check Portal Logs',
          badge:
            setttings.otherFeatures?.hasPortalErrors &&
            React.createElement(Badge, { colour: 'warning' }, 'Errors Detected'),
          onClick: () => {
            // Track the Mixpanel event for navigating to Purchase SMS Credits
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_PORTAL_LOGS, {
              Panel: NAVIGATION.PANEL_NAME.PORTAL_LOGS,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.PortalLogs,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'E-Newsletter Schedule',
          hasAccess: privileges.eNewsletterSchedule?.canAccess,
          onClick: () => {
            // Track the Mixpanel event for navigating to E-Newsletter Schedule
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_E_NEWSLETTER_SCHEDULE, {
              Panel: NAVIGATION.PANEL_NAME.E_NEWSLETTER_SCHEDULE,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.EnewsletterSchedule,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'Testimonials Manager',
          hasAccess: privileges.testimonials?.canManage,
          onClick: () => {
            // Track the Mixpanel event for navigating to Testimonials
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_TESTIMONIALS, {
              Panel: NAVIGATION.PANEL_NAME.TESTMONIALS,
              FromRoute: legacyCRMIframePageName,
            })
            LegacyCRMIframeOpenPanel({
              iframeLoaded: isLegacyCRMIframeLoaded,
              pageName: legacyCRMIframePageName,
              legacyLink: PanelLegacyLink.Testimonials,
              legacyCRMUrl,
            })
          },
        },
        {
          label: 'CMS Editor',
          hasAccess: privileges.cms?.canAccess,
          onClick: () => {
            // Track the Mixpanel event for navigating to CMS Editor
            trackMixpanelAction(NAVIGATION.ACTION.NAVIGATE_CMS_EDITOR, {
              DestinationRoute: buildCMSPageURL(),
              FromRoute: legacyCRMIframePageName ?? '',
            })
            navigate(buildCMSPageURL())
          },
        },
      ],
    },
    {
      label: 'group-external-links',
      group: [
        {
          label: 'Manage MFA',
          rightIcon: <ExportIcon />,
          onClick: () => {
            const manageMfaUrl = process.env.MANAGE_MFA_URL
            if (manageMfaUrl) {
              openNewPage(manageMfaUrl, NAVIGATION.SCREEN_NAME.MANAGE_MFA, legacyCRMIframePageName)()
            } else {
              error('Unable to access MFA Management. URL not configured - please contact support.')
            }
          },
        },
        {
          label: 'Terms & Conditions',
          hasAccess: privileges.termsAndConditions?.canAccess,
          rightIcon: <ExportIcon />,
          onClick: () => {
            const termsUrl = setttings.externalResources?.termsAndConditions?.url
            if (termsUrl) {
              openNewPage(termsUrl, NAVIGATION.SCREEN_NAME.TERMS_AND_CONDITIONS, legacyCRMIframePageName)()
            } else {
              error('Unable to access Terms & Conditions. URL not configured - please contact support.')
            }
          },
        },
      ],
    },
    {
      label: 'group-logout',
      group: [
        {
          label: 'Logout',
          onClick: handleLogoutClick,
        },
      ],
    },
  ]
}
